import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, endpoints } from "@/lib/api";
import { fmtMoney } from "@/lib/utils-format";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Phone, Mail, ArrowRight } from "lucide-react";

export default function Employees() {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({});
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});

  const load = useCallback(async () => {
    const r = await endpoints.list("employees");
    setItems(r.data);
    const all = await Promise.all(
      r.data.map((e) => api.get(`/employees/${e.id}/stats`).then((s) => [e.id, s.data]).catch(() => [e.id, null]))
    );
    setStats(Object.fromEntries(all));
  }, []);
  useEffect(() => { load(); }, [load]);

  const startNew = () => { setEditing(null); setForm({ first_name:"", last_name:"", role:"Crew Member", phone:"", email:"", pay_rate:20, crew:"Crew A", availability:"Mon-Fri", performance:"Good" }); setOpen(true); };
  const startEdit = (e) => { setEditing(e); setForm(e); setOpen(true); };
  const save = async () => {
    if (editing) await endpoints.update("employees", editing.id, form);
    else await endpoints.create("employees", form);
    toast.success("Employee saved"); setOpen(false); load();
  };
  const remove = async (id) => { if(!window.confirm("Delete?"))return; await endpoints.remove("employees", id); load(); };

  return (
    <Layout title="Employees" subtitle={`${items.length} active team members · Open a card for YTD + All-Time payroll details`}>
      <div className="flex justify-end mb-4"><Button onClick={startNew} data-testid="employees-new"><Plus className="size-4 mr-1.5"/>New Employee</Button></div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="employees-grid">
        {items.map((e) => {
          const s = stats[e.id];
          const ytd = s?.ytd || { balance: 0 };
          return (
            <Card key={e.id} className="p-5 rounded-xl">
              <div className="flex items-start justify-between">
                <Link to={`/employees/${e.id}`} className="flex items-center gap-3 group">
                  <div className="size-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-display font-bold">
                    {e.first_name?.[0]}{e.last_name?.[0]}
                  </div>
                  <div>
                    <div className="font-display font-semibold text-base group-hover:text-primary transition-colors">{e.first_name} {e.last_name}</div>
                    <div className="text-xs text-muted-foreground">{e.role} · {e.crew}</div>
                  </div>
                </Link>
                <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-secondary/15 text-secondary border border-secondary/30">{e.performance}</span>
              </div>

              <div className="mt-4 grid grid-cols-2 gap-2 text-[11px] text-muted-foreground">
                <div>Rate: <span className="font-mono font-semibold text-foreground">{fmtMoney(e.pay_rate || 0)}/hr</span></div>
                <div>Balance: <span className={`font-mono font-semibold ${ytd.balance > 0 ? "text-amber-600 dark:text-amber-400" : "text-secondary"}`}>{fmtMoney(ytd.balance)}</span></div>
              </div>
              <div className="flex items-center gap-3 mt-3 text-[11px] text-muted-foreground">
                <span className="flex items-center gap-1"><Phone className="size-3"/>{e.phone}</span>
                <span className="flex items-center gap-1 truncate"><Mail className="size-3"/>{e.email}</span>
              </div>

              <div className="flex gap-2 mt-3 pt-3 border-t border-border">
                <Link to={`/employees/${e.id}`} className="flex-1">
                  <Button size="sm" variant="outline" className="w-full" data-testid={`employee-details-${e.id}`}>View Details <ArrowRight className="size-3 ml-1"/></Button>
                </Link>
                <Button size="sm" variant="ghost" onClick={()=>startEdit(e)}><Pencil className="size-3.5"/></Button>
                <Button size="sm" variant="ghost" className="text-destructive" onClick={()=>remove(e.id)}><Trash2 className="size-3.5"/></Button>
              </div>
            </Card>
          );
        })}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="font-display text-2xl">{editing?"Edit Employee":"New Employee"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5"><Label>First Name</Label><Input value={form.first_name||""} onChange={(e)=>setForm({...form, first_name:e.target.value})}/></div>
            <div className="space-y-1.5"><Label>Last Name</Label><Input value={form.last_name||""} onChange={(e)=>setForm({...form, last_name:e.target.value})}/></div>
            <div className="space-y-1.5"><Label>Role</Label>
              <Select value={form.role||"Crew Member"} onValueChange={(v)=>setForm({...form, role:v})}>
                <SelectTrigger><SelectValue/></SelectTrigger>
                <SelectContent>{["Crew Lead","Crew Member","Designer","Manager","Admin"].map(r=><SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Crew</Label>
              <Select value={form.crew||"Crew A"} onValueChange={(v)=>setForm({...form, crew:v})}>
                <SelectTrigger><SelectValue/></SelectTrigger>
                <SelectContent>{["Crew A","Crew B","Design","Solo"].map(c=><SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Phone</Label><Input value={form.phone||""} onChange={(e)=>setForm({...form, phone:e.target.value})}/></div>
            <div className="space-y-1.5"><Label>Email</Label><Input value={form.email||""} onChange={(e)=>setForm({...form, email:e.target.value})}/></div>
            <div className="space-y-1.5"><Label>Hourly Rate ($/hr)</Label><Input type="number" step="0.5" value={form.pay_rate||0} onChange={(e)=>setForm({...form, pay_rate:Number(e.target.value)})}/></div>
            <div className="space-y-1.5"><Label>Availability</Label><Input value={form.availability||""} onChange={(e)=>setForm({...form, availability:e.target.value})}/></div>
            <div className="space-y-1.5"><Label>Performance</Label>
              <Select value={form.performance||"Good"} onValueChange={(v)=>setForm({...form, performance:v})}>
                <SelectTrigger><SelectValue/></SelectTrigger>
                <SelectContent>{["Excellent","Good","Needs Improvement"].map(p=><SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={()=>setOpen(false)}>Cancel</Button><Button onClick={save} data-testid="employee-save">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
